/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from '@/assets/images/logo.png';
import logoSticky from '@/assets/images/logo-sticky.png';
import onlineStoreBanner from '@/assets/images/online-store-banner.png';
import marySeaScholarship from '@/assets/images/mary-shea-scholarship.png';
import anastaziaMarcy from '@/assets/images/anastazia-marcy.jpg';
import runwayWebcam from '@/assets/images/runway-webcam.jpg';
import ianOConnorTraining from '@/assets/images/ian-oconnor-training.jpg';
import orenJenkinsTraining from '@/assets/images/oren-jenkins-training.jpg';
import charlieHruskaTraining from '@/assets/images/charlie-hruska-training.jpg';
import miaNagelTraining from '@/assets/images/mia-nagel-training.jpg';
import emilyPaquetteTraining from '@/assets/images/emily-paquette-training.jpg';
import brianMaintenance from '@/assets/images/brian-maintenance.jpg';
import aircraftTraining from '@/assets/images/aircraft-training.jpeg';

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo
  '/wp-content/uploads/logo-sticky.png': logoSticky,
  '/images/logo.png': logo,
  '/images/logo-sticky.png': logoSticky,
  '/assets/images/logo.png': logo,
  '/assets/images/logo-sticky.png': logoSticky,
  
  // Online store banner
  '/wp-content/uploads/online-store-website-31-1200x1553.png': onlineStoreBanner,
  '/images/online-store-banner.png': onlineStoreBanner,
  '/assets/images/online-store-banner.png': onlineStoreBanner,
  
  // News article images
  '/images/mary-shea-scholarship.png': marySeaScholarship,
  '/assets/images/mary-shea-scholarship.png': marySeaScholarship,
  '/images/anastazia-marcy.jpg': anastaziaMarcy,
  '/assets/images/anastazia-marcy.jpg': anastaziaMarcy,
  
  // Webcam images
  '/api/Camera/LastImageaspx/shareID17005924/AnyName.jpg': runwayWebcam,
  '/images/runway-webcam.jpg': runwayWebcam,
  '/assets/images/runway-webcam.jpg': runwayWebcam,
  
  // Student milestone images
  '/wp-content/uploads/IMG_9304-600x338.jpeg': ianOConnorTraining,
  '/wp-content/uploads/IMG_9304-800x450.jpeg': ianOConnorTraining,
  '/images/ian-oconnor-training.jpg': ianOConnorTraining,
  '/assets/images/ian-oconnor-training.jpg': ianOConnorTraining,
  
  '/wp-content/uploads/IMG_9224-500x383.jpeg': orenJenkinsTraining,
  '/wp-content/uploads/IMG_9224-800x450.jpeg': orenJenkinsTraining,
  '/images/oren-jenkins-training.jpg': orenJenkinsTraining,
  '/assets/images/oren-jenkins-training.jpg': orenJenkinsTraining,
  
  '/wp-content/uploads/IMG_6374-500x383.jpeg': charlieHruskaTraining,
  '/wp-content/uploads/IMG_6374-800x450.jpeg': charlieHruskaTraining,
  '/images/charlie-hruska-training.jpg': charlieHruskaTraining,
  '/assets/images/charlie-hruska-training.jpg': charlieHruskaTraining,
  
  '/wp-content/uploads/IMG_0842-500x383.jpeg': miaNagelTraining,
  '/wp-content/uploads/IMG_0842-800x450.jpeg': miaNagelTraining,
  '/images/mia-nagel-training.jpg': miaNagelTraining,
  '/assets/images/mia-nagel-training.jpg': miaNagelTraining,
  
  '/wp-content/uploads/IMG_6556-800x450.jpeg': emilyPaquetteTraining,
  '/images/emily-paquette-training.jpg': emilyPaquetteTraining,
  '/assets/images/emily-paquette-training.jpg': emilyPaquetteTraining,
  
  // Services images
  '/wp-content/uploads/brian-maintenance.jpg': brianMaintenance,
  '/images/brian-maintenance.jpg': brianMaintenance,
  '/assets/images/brian-maintenance.jpg': brianMaintenance,
  
  '/wp-content/uploads/IMG_0542-600x338.jpeg': aircraftTraining,
  '/images/aircraft-training.jpeg': aircraftTraining,
  '/assets/images/aircraft-training.jpeg': aircraftTraining,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
